import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useQueryClient } from '../lib/queryClient';
import { Bot, AlertCircle, Loader2, ListOrdered, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAiChatHistory, useSendAiMessage, useUpdateAiConfig } from '../helpers/useAiChat';
import { useSession } from '../helpers/useSessionsApi';
import { useCreateTodo } from '../helpers/useTodosApi';
import { useAiMessageQueue } from '../helpers/useAiMessageQueue';
import {
  useCreateCheckpoint,
  useRestoreCheckpoint,
  useCheckpointPreview,
} from '../helpers/useCheckpointsApi';
import { usePresenceCentralized } from '../helpers/usePresenceApi';
import { useCurrentAiActivity } from '../helpers/useAiActivityApi';
import { useSessionAiParticipants } from '../helpers/useSessionAiParticipants';
import { useAuth } from '../helpers/useAuth';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';

import { Button } from './Button';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';
import { Badge } from './Badge';
import { AnnotationSummary } from './AiChatAnnotationSelector';
import { ModelSelection } from './ModelSelector';
import { AiChatMessage } from './AiChatMessage';
import { AiChatInput } from './AiChatInput';
import { CheckpointPreviewDialog } from './CheckpointPreviewDialog';
import { FileDiffViewer } from './FileDiffViewer';
import { SearchResult } from './WebSearchResults';
import { Selectable } from 'kysely';
import { SessionFiles } from '../helpers/schema';
import { parseSlashCommand, getCommandInfo, getRandomTip } from '../helpers/slashCommands';
import styles from './AiChatPanel.module.css';
import { CommandListModal } from './CommandListModal';
import { useAiVoiceMuted } from './AiVoiceControls';
import { useRealtimeSession } from '../helpers/useRealtimeSession';
import { getVoiceForPersona, cleanTextForTTS } from '../helpers/useInworldTTS';
import { useTTS } from '../helpers/useTTS';
import { loadInstalledSkills, saveInstalledSkills } from './SkillSearchModal';
import type { InstalledSkill } from './SkillSearchModal';

// Consistent RoboHash avatar from personaId (shared pattern with CombinedChatPanel + CollaboratorChat)
const _hashStr = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h = h & h;
  }
  return Math.abs(h);
};
const getRobotAvatar = (seed: string | number): string =>
  `https://robohash.org/${typeof seed === 'string' ? _hashStr(seed) : seed}.png?set=set1&size=64x64`;

interface AiChatPanelProps {
  sessionId: string;
  isOwner?: boolean;
  currentFile?: string; // Path of the currently open file
  className?: string;
  annotations?: AnnotationSummary[];
  initialAttachment?: { content: string; name?: string; textNotes?: string[] } | null;
  onAttachmentSent?: () => void;
  onRequestAnnotateAttachment?: (dataUrl: string) => void;
  autoExecute?: boolean;
  onAutoExecuteChange?: (enabled: boolean) => void;
  onSendMessageRef?: (sendFn: (message: string) => void) => void; // Expose sendMessage function
  sessionFiles?: Selectable<SessionFiles>[]; // Current session files for diff viewing
  onOpenSkillSearch?: (query?: string) => void;
  onOpenSkillTree?: (mode?: 'view' | 'create') => void;
  activeSkills?: InstalledSkill[];
  onActiveSkillsChange?: (skills: InstalledSkill[]) => void;
  onRequestScreenshot?: () => void; // For AI to request preview screenshots
  previewScreenshot?: string | null; // Current preview screenshot for display
}

export const AiChatPanel: React.FC<AiChatPanelProps> = ({
  sessionId,
  isOwner,
  currentFile,
  className,
  annotations,
  initialAttachment,
  onAttachmentSent,
  onRequestAnnotateAttachment,
  autoExecute,
  onAutoExecuteChange,
  onSendMessageRef,
  sessionFiles = [],
  onOpenSkillSearch,
  onOpenSkillTree,
  activeSkills = [],
  onActiveSkillsChange,
  onRequestScreenshot,
  previewScreenshot,
}) => {
  const queryClient = useQueryClient();
  const { data: history, isLoading, error, isError } = useAiChatHistory(sessionId);
  const { data: presenceData } = usePresenceCentralized(sessionId);
  const { isActive: hasAiActivity, currentActivity } = useCurrentAiActivity(sessionId);
  const { authState } = useAuth();
  const prevAiActivityRef = useRef(hasAiActivity);
  const lastDispatchedSuggestionSignatureRef = useRef<string | null>(null);

  const [isCmdListOpen, setIsCmdListOpen] = useState(false);

  // Voice TTS for AI participants (auto-speak collab responses)
  const isMuted = useAiVoiceMuted();
  const { speak, stop: stopSpeaking, isPlaying: isSpeaking } = useTTS();
  const lastSpokenMessageRef = useRef<string | null>(null);
  const ttsQueueRef = useRef<Promise<void>>(Promise.resolve());
  const [speakingPersonaId, setSpeakingPersonaId] = useState<number | null>(null);
  const [ttsQueue, setTtsQueue] = useState<Array<{ personaId: number; personaName: string; preview: string }>>([]);

  // Real-time updates via WebSocket for AI participant messages
  const { lastMessage: realtimeMessage } = useRealtimeSession(sessionId);

  // Handle real-time AI participant messages - auto-speak and refresh
  useEffect(() => {
    if (!realtimeMessage) return;
    try {
      const data =
        typeof realtimeMessage === 'string' ? JSON.parse(realtimeMessage) : realtimeMessage;

      // AI participant message received - refresh history and auto-speak
      if (data.type === 'ai_chat_message') {
        queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
        queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });

        // Auto-speak the AI participant's message (if not muted)
        // Queue messages so multiple participants can speak in sequence
        if (!isMuted && data.personaId && data.content) {
          const messageId = `${data.personaId}-${data.timestamp}`;
          if (lastSpokenMessageRef.current !== messageId) {
            lastSpokenMessageRef.current = messageId;
            const cleanedText = cleanTextForTTS(data.content);
            if (cleanedText.length > 10) {
              const voiceId = getVoiceForPersona(data.personaId, data.personaName);
              // Queue speech — don't block, let multiple participants speak in order
              const queueEntry = {
                personaId: data.personaId,
                personaName: data.personaName || `Participant ${data.personaId}`,
                preview: cleanedText.slice(0, 60) + (cleanedText.length > 60 ? '...' : ''),
              };
              setTtsQueue(q => [...q, queueEntry]);

              const speakTask = async () => {
                setSpeakingPersonaId(data.personaId);
                try {
                  await speak(cleanedText, { voiceId });
                } finally {
                  setSpeakingPersonaId(null);
                  setTtsQueue(q => q.filter(e => e !== queueEntry));
                }
              };
              if (!ttsQueueRef.current) ttsQueueRef.current = Promise.resolve();
              ttsQueueRef.current = ttsQueueRef.current.then(speakTask).catch(() => {
                setTtsQueue(q => q.filter(e => e !== queueEntry));
              });
            }
          }
        }
      }

      // File changes from AI participants
      if (data.type === 'file_changed' || data.type === 'file_created') {
        queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });
      }
    } catch (_) {
      // Ignore parse errors
    }
  }, [realtimeMessage, queryClient, sessionId, isMuted, speak]);

  // State for viewing file diffs
  const [diffViewerState, setDiffViewerState] = useState<{
    open: boolean;
    checkpointId: string | null;
    filename: string | null;
  }>({ open: false, checkpointId: null, filename: null });

  // Fetch checkpoint data when diff viewer is open
  const { data: diffCheckpointData } = useCheckpointPreview(diffViewerState.checkpointId || null);

  // When AI activity completes, invalidate file list to show new files immediately
  useEffect(() => {
    if (prevAiActivityRef.current && !hasAiActivity) {
      // AI just finished working - refresh file list to show new files
      queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });
      queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
    }
    prevAiActivityRef.current = hasAiActivity;
  }, [hasAiActivity, queryClient, sessionId]);

  // Get current user info for avatar fallback
  const currentUser = authState.type === 'authenticated' ? authState.user : null;

  // Fetch session data and persist model selection to DB
  const { data: sessionData } = useSession(sessionId);
  const { mutate: updateAiConfig } = useUpdateAiConfig();

  // Load model selection from session DB, falling back to localStorage then default
  const [selectedModel, setSelectedModel] = useState<ModelSelection | undefined>(undefined);
  const sessionModelInitialized = useRef(false);

  useEffect(() => {
    if (sessionModelInitialized.current) return;
    // Wait for session data to load
    if (!sessionData) return;
    sessionModelInitialized.current = true;

    const session = sessionData.session;
    // Guard: reject stale OpenAI models from DB/localStorage — we use Ollama Cloud only
    const OPENAI_MODELS = [
      'gpt-4o',
      'gpt-4o-mini',
      'gpt-4-turbo',
      'gpt-4',
      'gpt-3.5-turbo',
      'o1',
      'o3',
    ];
    const isOpenAiModel = (m: string) => OPENAI_MODELS.some((om) => m.startsWith(om));
    const DEFAULT_MODEL: ModelSelection = {
      provider: 'ollama_cloud',
      model: 'glm-5.1',
      isLocal: false,
    };

    // Use session DB values if available
    if (session.aiModel && session.aiProvider) {
      if (isOpenAiModel(session.aiModel)) {
        setSelectedModel(DEFAULT_MODEL);
      } else {
        setSelectedModel({
          provider: session.aiProvider,
          model: session.aiModel,
          isLocal: session.aiProvider === 'ollama_local',
        });
      }
      return;
    }
    // Fall back to localStorage
    try {
      const saved = localStorage.getItem(`ai-model-selection-${sessionId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (
          parsed &&
          typeof parsed.provider === 'string' &&
          typeof parsed.model === 'string' &&
          typeof parsed.isLocal === 'boolean'
        ) {
          if (isOpenAiModel(parsed.model)) {
            setSelectedModel(DEFAULT_MODEL);
          } else {
            setSelectedModel(parsed as ModelSelection);
          }
          return;
        }
      }
    } catch (e) {
      console.error('Failed to load saved model selection:', e);
    }
    // Default
    setSelectedModel(DEFAULT_MODEL);
  }, [sessionData, sessionId]);

  // Persist model selection to DB and localStorage whenever user changes it
  const handleModelChange = useCallback(
    (model: ModelSelection | undefined) => {
      setSelectedModel(model);
      if (model) {
        // Save to DB
        updateAiConfig({
          sessionId,
          aiEnabled: true,
          aiProvider: model.provider,
          aiModel: model.model,
        });
        // Also save to localStorage as a fast cache
        try {
          localStorage.setItem(`ai-model-selection-${sessionId}`, JSON.stringify(model));
        } catch (e) {
          void e;
        }
      }
    },
    [sessionId, updateAiConfig],
  );

  const [localAutoExecute, setLocalAutoExecute] = useState(true); // fallback when prop not provided
  const [mode, setMode] = useState<'generate' | 'chat'>('generate'); // Start with code generation mode by default
  const [webSearchEnabled, setWebSearchEnabled] = useState(false); // Web search toggle state
  const autoLoop = mode === 'generate'; // Enable continuous execution in generate mode — AI keeps building
  // FORCE auto-execute ON for generate mode - files should be created automatically
  const autoExecuteValue = mode === 'generate' ? true : (autoExecute ?? localAutoExecute);
  const createCheckpointMutation = useCreateCheckpoint();
  const restoreCheckpointMutation = useRestoreCheckpoint();

  const [previewCheckpointId, setPreviewCheckpointId] = useState<string | null>(null);
  const [isApplyingSuggestion, setIsApplyingSuggestion] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrollRef = useRef<number>(0);
  // Track whether the user has scrolled away from the bottom to avoid forcing auto-scroll
  const isAtBottomRef = useRef<boolean>(true);

  // Create todos from AI steps automatically
  const createTodoMutation = useCreateTodo();
  const handleCreateTodoFromStep = useCallback(
    async (stepText: string) => {
      try {
        await createTodoMutation.mutateAsync({
          sessionId,
          title: stepText.length > 100 ? stepText.substring(0, 97) + '...' : stepText,
          description: stepText,
        });
      } catch (e) {
        console.error('[AiChatPanel] Failed to create todo from step:', e);
      }
    },
    [sessionId, createTodoMutation],
  );

  const { sendMessage, streamingMessage, stopGeneration, applySuggestion } = useSendAiMessage(
    sessionId,
    autoExecuteValue,
    mode,
    handleCreateTodoFromStep, // Auto-create todos from AI steps
    autoLoop, // Auto loop mode: AI executes steps automatically
  );

  // Fetch AI participants for /agents command
  const { data: aiParticipants } = useSessionAiParticipants(sessionId);
  const hasActiveParticipants =
    aiParticipants && aiParticipants.filter((p) => p.isActive).length > 0;

  // Non-blocking message queue - allows users to send messages while AI is working
  const {
    queue: messageQueue,
    pendingCount,
    processingCount,
    hasActiveWork,
    activeStreams,
    enqueueMessage,
    stopMessage,
    stopAll,
    clearCompleted,
  } = useAiMessageQueue({
    sessionId,
    maxConcurrent: 2, // Allow 2 concurrent requests for responsiveness
    autoExecute: autoExecuteValue,
    mode,
    selectedModel,
    autonomousMode: hasActiveParticipants && mode !== 'chat', // Enable AI participants to respond
    activeSkills:
      activeSkills.length > 0
        ? activeSkills.map((s) => ({
            name: s.name,
            content: s.content,
            description: s.description,
          }))
        : undefined,
    onMessageComplete: (messageId, response) => {
      // When a queued message completes, refresh data
      queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });
      queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
      // Clean up completed streams after a short delay to allow DB history to refresh.
      // This prevents stale entries from lingering in activeStreams.
      setTimeout(() => clearCompleted(), 2000);
    },
  });

  // Track if we should use queue mode (when there's active work)
  const useQueueMode = hasActiveWork || !!streamingMessage;

  // Auto-scroll to bottom on session change or checkpoint creation, but avoid forcing while user scrolled up
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Only auto-scroll if user is at (or near) bottom
    if (isAtBottomRef.current) {
      try {
        el.scrollTop = el.scrollHeight;
      } catch (_: any) {
        void _;
      }
    }
  }, [sessionId]);

  // Scroll scheduler helper (respects user manual scroll)
  const scheduleScrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    // If user scrolled up, don't force scroll to bottom
    if (!isAtBottomRef.current) return;

    const MIN_INTERVAL = 100; // ms
    const now = Date.now();
    const since = now - (lastScrollRef.current || 0);

    if (since > MIN_INTERVAL) {
      try {
        el.scrollTop = el.scrollHeight;
      } catch (_: any) {
        void _;
      }
      lastScrollRef.current = Date.now();
      return;
    }

    // Otherwise schedule a delayed scroll to avoid jitter
    if ((scheduleScrollToBottom as any)._timer)
      clearTimeout((scheduleScrollToBottom as any)._timer);
    (scheduleScrollToBottom as any)._timer = setTimeout(
      () => {
        try {
          el.scrollTop = el.scrollHeight;
        } catch (_: any) {
          void _;
        }
        lastScrollRef.current = Date.now();
      },
      MIN_INTERVAL - since + 10,
    );
  };

  // Track manual user scrolls to prevent auto-scrolling while user is browsing history
  const handleScroll = (e?: React.UIEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    isAtBottomRef.current = atBottom;
  };

  // Auto-scroll to bottom with frame-based scheduling for smoother realtime updates.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let rafId = 0;
    const scrollToBottom = () => {
      // Respect user's manual scroll position: only auto-scroll when at/near bottom.
      if (!isAtBottomRef.current) return;
      try {
        el.scrollTop = el.scrollHeight;
      } catch (e) {
        // ignore
      }
    };
    rafId = requestAnimationFrame(scrollToBottom);
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [
    history?.messages?.length,
    streamingMessage?.id,
    streamingMessage?.content,
    activeStreams.size,
    messageQueue.length,
  ]);

  // Keep bottom lock stable when message content height changes after render
  // (images load, code blocks expand, streaming chunks append).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (!isAtBottomRef.current) return;
      try {
        el.scrollTop = el.scrollHeight;
      } catch {
        // ignore
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // State for local system messages (from commands)
  const [localSystemMessage, setLocalSystemMessage] = useState<string | null>(null);

  // Handle built-in slash commands
  const handleBuiltinCommand = (command: string, args: string): boolean => {
    switch (command) {
      case '/cmdlist':
      case '/commands':
        // Show command list as a modal
        toast.success('Showing slash commands');
        setIsCmdListOpen(true);
        return true;

      case '/help': {
        const helpText = args
          ? `Getting help for: ${args}\n\nType your question and I'll assist you.`
          : `## Help\n\nWelcome to Tasking.tech AI Chat!\n\n### Quick Tips\n- Use \`/cmdlist\` to see all available commands\n- Use \`@name\` to mention AI participants\n- Drag & drop images to share them\n- Toggle Auto-Execute (⚡) for automatic code execution\n\n${getRandomTip()}`;
        setLocalSystemMessage(helpText);
        return true;
      }

      case '/clear':
        // Clear local display (doesn't delete from DB)
        toast.info('Chat display cleared');
        return true;

      case '/mode':
        if (args === 'chat' || args === 'generate') {
          setMode(args as 'generate' | 'chat');
          toast.success(`Switched to ${args} mode`);
        } else if (args === 'code') {
          setMode('generate');
          toast.success('Switched to generate mode');
        } else {
          toast.info(`Current mode: ${mode}. Use /mode chat or /mode generate to switch.`);
        }
        return true;

      case '/checkpoint': {
        const checkpointName =
          args || `Checkpoint - ${new Date().toLocaleTimeString('en-US')}`;
        handleCreateCheckpoint(checkpointName);
        toast.success(`Creating checkpoint: ${checkpointName}`);
        return true;
      }

      case '/model': {
        const currentModelName = selectedModel?.model || 'default';
        const currentProvider = selectedModel?.provider || 'anthropic';
        setLocalSystemMessage(
          `## Current Model\n\n**Model:** ${currentModelName}\n**Provider:** ${currentProvider}\n**Local:** ${selectedModel?.isLocal ? 'Yes' : 'No'}\n\nUse the model selector in the toolbar to change models.`,
        );
        return true;
      }

      case '/find':
      case '/skills':
        if (onOpenSkillSearch) {
          onOpenSkillSearch(args || undefined);
        } else {
          toast.info('Skill search is not available in this view.');
        }
        return true;

      case '/skilltree':
      case '/tree':
      case '/packs':
      case '/skill-packs':
      case '/skillpack':
        if (onOpenSkillTree) {
          onOpenSkillTree('view');
        } else {
          toast.info('Skill tree is not available in this view.');
        }
        return true;

      case '/create-pack':
      case '/new-pack':
        if (onOpenSkillTree) {
          onOpenSkillTree('create');
        } else {
          toast.info('Skill tree is not available in this view.');
        }
        return true;

      case '/install': {
        if (!args) {
          toast.error(
            'Usage: /install <package-names>  (e.g. /install lucide-react framer-motion)',
          );
          return true;
        }
        toast.info('Package installation is handled automatically when package.json changes.');
        return true;
      }

      case '/add': {
        if (!args) {
          toast.error('Usage: /add <repo-url> [--skill <name>]');
          return true;
        }
        toast.info('Skill installation is handled via the Skills panel.');
        return true;
      }

      case '/use-skill':
      case '/toggle-skill':
      case '/skill-list':
      case '/my-skills': {
        if (!args) {
          // No args: show active skills list
          if (activeSkills.length === 0) {
            setLocalSystemMessage(
              '## No Active Skills\n\nNo agent skills are currently active.\n\nUse `/find` or `/skills` to browse and install skills, or `/use-skill <name> on` to activate one.',
            );
            if (onOpenSkillSearch) onOpenSkillSearch();
          } else {
            const list = activeSkills.map((s) => `- **${s.name}**: ${s.description}`).join('\n');
            setLocalSystemMessage(
              `## Active Skills (${activeSkills.length})\n\n${list}\n\nUse \`/use-skill <name> off\` to deactivate, or \`/find\` to install new skills.`,
            );
            // Keep behavior consistent: slash commands that manage skills should open the skills modal.
            if (onOpenSkillSearch) onOpenSkillSearch();
          }
          return true;
        }
        // Args provided: toggle specific skill
        const parts = args.split(/\s+/);
        const skillName = parts[0];
        const toggleStr = parts[1];
        const allSkills = loadInstalledSkills();
        const skill = allSkills.find((s) => s.name.toLowerCase() === skillName.toLowerCase());
        if (!skill) {
          // Skill not found — open search with query
          setLocalSystemMessage(
            `## Skill Not Found\n\nNo installed skill named **${skillName}**. Opening skill search...`,
          );
          if (onOpenSkillSearch) onOpenSkillSearch(skillName);
          return true;
        }
        const newState = toggleStr === 'off' ? false : toggleStr === 'on' ? true : !skill.active;
        const updated = allSkills.map((s) =>
          s.name === skill.name ? { ...s, active: newState } : s,
        );
        saveInstalledSkills(updated);
        onActiveSkillsChange?.(updated.filter((s) => s.active));
        setLocalSystemMessage(`Skill **${skill.name}** ${newState ? 'activated' : 'deactivated'}.`);
        return true;
      }

      case '/agents':
      case '/who':
        if (aiParticipants && aiParticipants.length > 0) {
          const agentList = aiParticipants
            .map((p) => `- **${p.personaName}** (${p.personaCategory})`)
            .join('\n');
          setLocalSystemMessage(
            `## Active AI Participants\n\n${agentList}\n\nUse @name to mention them directly.`,
          );
        } else {
          setLocalSystemMessage(
            '## No AI Participants\n\nNo AI participants are active in this session.\n\nAdd participants using the persona selector in the chat toolbar.',
          );
        }
        return true;

      case '/goal':
      case '/objective':
        if (args) {
          setLocalSystemMessage(
            `## Goal Updated\n\nSession goal set to: **${args}**\n\nThe AI will keep this objective in mind.`,
          );
        } else {
          setLocalSystemMessage(
            '## Session Goal\n\nNo goal set yet. Use `/goal <objective>` to define one.',
          );
        }
        return true;

      case '/status':
        setLocalSystemMessage(
          `## Session Status\n\n` +
            `**Session:** ${sessionId}\n` +
            `**Mode:** ${mode}\n` +
            `**Model:** ${selectedModel?.model || 'default'}\n` +
            `**Auto-Execute:** ${autoExecute ? 'On' : 'Off'}\n` +
            `**Active Skills:** ${activeSkills.length}\n` +
            `**AI Participants:** ${aiParticipants?.length || 0}\n` +
            `**AI Activity:** ${hasAiActivity ? `Yes — ${currentActivity?.status || 'working'}` : 'None'}`,
        );
        return true;

      case '/stop':
      case '/halt':
        toast.info('Stopping current AI work...');
        return true;

      case '/context':
        setLocalSystemMessage(
          `## Session Context\n\n` +
            `**Session ID:** ${sessionId}\n` +
            `**Current File:** ${currentFile || 'None'}\n` +
            `**Files in Session:** ${sessionFiles.length}\n` +
            `**Mode:** ${mode}`,
        );
        return true;

      case '/branch':
        toast.info(
          'Branch management is available through GitHub integration in the session settings.',
        );
        return true;

      case '/pickup':
        if (args) {
          toast.success(`Picking up task: ${args}`);
        } else {
          toast.info('Usage: /pickup <task-name> — assign a todo to yourself');
        }
        return true;

      case '/drop':
        if (args) {
          toast.success(`Dropped task: ${args}`);
        } else {
          toast.info('Usage: /drop <task-name> — release a claimed todo');
        }
        return true;

      case '/fire':
        if (args) {
          toast.info(`Removing participant: ${args}`);
        } else {
          toast.info('Usage: /fire <participant-name> — remove an AI participant');
        }
        return true;

      case '/auto':
        if (onAutoExecuteChange) {
          const newState = args === 'off' ? false : args === 'on' ? true : !autoExecute;
          onAutoExecuteChange(newState);
          toast.success(`Auto-execute ${newState ? 'enabled' : 'disabled'}`);
        } else {
          toast.info('Auto-execute toggle is not available.');
        }
        return true;

      case '/limit':
        if (args) {
          toast.success(`Autonomous step limit set to: ${args}`);
        } else {
          toast.info('Usage: /limit <number> — set max autonomous steps');
        }
        return true;

      case '/rollback':
        if (args) {
          toast.info(`Rolling back to checkpoint: ${args}`);
        } else {
          toast.info('Usage: /rollback <checkpoint-id> — restore a previous checkpoint');
        }
        return true;

      case '/retract':
        toast.info('Retracting last submission...');
        return true;

      case '/reset':
        toast.info('Session context reset. Start a fresh conversation.');
        return true;

      case '/mute':
        if (args) {
          toast.success(`Muted: ${args}`);
        } else {
          toast.info('Usage: /mute <participant-name> — mute an AI participant');
        }
        return true;

      case '/unmute':
        if (args) {
          toast.success(`Unmuted: ${args}`);
        } else {
          toast.info('Usage: /unmute <participant-name> — unmute an AI participant');
        }
        return true;

      case '/tip':
        setLocalSystemMessage(`## Tip\n\n${getRandomTip()}`);
        return true;

      default:
        return false;
    }
  };

  const handleSend = async (
    message: string,
    context: string[],
    attachment?: { content: string; name: string; addToProject?: boolean },
    webSearchResults?: SearchResult[],
  ) => {
    // If user is sending a message, force scroll to bottom so they see the result
    isAtBottomRef.current = true;

    // Clear any local system message when user sends a new message
    setLocalSystemMessage(null);

    // Check for slash commands (works anywhere in the message)
    const parsed = parseSlashCommand(message);
    if (parsed) {
      const cmdInfo = getCommandInfo(parsed.command);

      if (cmdInfo) {
        // Use canonical command to handle aliases (e.g. /start -> /work)
        const canonicalCommand = cmdInfo.command;

        if (cmdInfo.action === 'builtin') {
          // Handle builtin command locally
          if (handleBuiltinCommand(canonicalCommand, parsed.args)) {
            // If there was text before the command, send it as a regular message
            if (parsed.prefix) {
              message = parsed.prefix;
            } else {
              return; // Command handled, no extra text to send
            }
          }
        }
        // For AI commands, transform the message to be more explicit
        // e.g., "/review index.js" becomes "Please review the code in index.js..."
        if (cmdInfo.action === 'ai') {
          const enhancedMessage = transformAiCommand(canonicalCommand, parsed.args, currentFile);
          // Combine prefix text with the transformed command
          message = parsed.prefix ? `${parsed.prefix}\n\n${enhancedMessage}` : enhancedMessage;
        }
      } else {
        // Unknown command - show help
        toast.warning(
          `Unknown command: ${parsed.command}. Use /cmdlist to see available commands.`,
        );
        return;
      }
    }

    // Build skills array to pass via dedicated field (injected into system prompt, not every message)
    const skillsPayload =
      activeSkills.length > 0
        ? activeSkills.map((s) => ({
            name: s.name,
            content: s.content,
            description: s.description,
          }))
        : undefined;

    // If there's already active work (streaming or processing), queue the message
    // This allows non-blocking chat - users can keep sending while AI works
    if (useQueueMode) {
      // Queue the message for processing
      enqueueMessage(message, context, attachment, webSearchResults);
      // Ensure we schedule a scroll after the message is appended
      scheduleScrollToBottom();

      if (onAttachmentSent) {
        try {
          onAttachmentSent();
        } catch (e) {
          // ignore
        }
      }
      return;
    }

    // No active work - send directly for immediate response
    // Check if we should enable AI participant responses
    const hasActiveParticipants =
      aiParticipants && aiParticipants.filter((p) => p.isActive).length > 0;

    await sendMessage(
      message,
      context,
      selectedModel
        ? {
            useLocalLlm: selectedModel.isLocal,
            provider: selectedModel.isLocal
              ? 'ollama_local'
              : selectedModel.provider || 'ollama_cloud',
            model: selectedModel.model,
            mode: mode, // Pass mode to backend
          }
        : { mode: mode }, // Pass mode even if no model selected
      attachment,
      webSearchResults,
      skillsPayload,
    );

    // Ensure we schedule a scroll after the message is appended
    scheduleScrollToBottom();

    if (onAttachmentSent) {
      try {
        onAttachmentSent();
      } catch (e) {
        // ignore
      }
    }
  };

  // Transform AI slash commands into explicit prompts
  function transformAiCommand(command: string, args: string, currentFile?: string): string {
    const fileRef = args || currentFile || 'the current code';

    switch (command) {
      case '/work':
        return `Start an AI team build session. Goal: ${args || 'Build the project'}`;
      case '/discuss':
        return `Let's discuss: ${args || 'the current state of the project'}`;
      case '/brainstorm':
        return `Brainstorm ideas for: ${args || 'the next features'}`;
      case '/huddle':
        return 'Quick team sync - please provide status updates from all active agents.';
      case '/focus':
        return `Focus strictly on this task: ${args}`;
      case '/plan':
        return `Create a detailed, structured implementation plan for: ${args || 'the current project'}.

Format the plan as follows:

## Project Overview
Brief description of what we're building.

## Tech Stack
List the technologies, frameworks, and libraries to use.

## File Structure
Show the directory tree of files to create/modify.

## Implementation Steps
Numbered steps with:
- What to build in each step
- Which files to create/modify
- Key code patterns to use

## UI/UX Plan
- Page layouts and component hierarchy
- Design tokens (colors, typography, spacing)
- Responsive breakpoints

## API Endpoints
List any backend routes needed.

## Testing Plan
How to verify each feature works.

Be specific and actionable. Reference existing files in the session when applicable.`;
      case '/pr':
        return `Prepare a pull request description for: ${args || 'recent changes'}`;
      case '/assign':
        return `Assign task to agent: ${args}`;
      case '/history':
        return 'Summarize the recent session activity, key decisions, and completed tasks.';
      case '/summary':
        return `Summarize the current session${args ? `, focusing on: ${args}` : ''}. Include goals, decisions, and changes.`;
      case '/review':
        return `Please review ${fileRef} for potential issues, bugs, best practices, and improvements.`;
      case '/explain':
        return `Please explain how ${fileRef} works. Break down the logic and explain the key concepts.`;
      case '/refactor':
        return `Please refactor ${fileRef}. ${args ? `Focus on: ${args}` : 'Improve code quality, readability, and maintainability.'}`;
      case '/test':
        return `Please write comprehensive unit tests for ${fileRef}.`;
      case '/fix':
        return `Please help fix this issue: ${args || 'There seems to be an error in the code.'}`;
      case '/optimize':
        return `Please optimize ${fileRef} for better performance.`;
      case '/document':
        return `Please add documentation and comments to ${fileRef}.`;
      case '/scaffold':
        return `Please scaffold a new ${args || 'component'}. Create the necessary files and boilerplate code.`;
      case '/debug':
        return `Please help debug this issue: ${args || 'Something is not working as expected.'}`;
      case '/todo':
        return `Please add a todo item: ${args}`;
      case '/files':
        return 'Please list all the files in the current session and give a brief description of each.';
      case '/docs':
        return `Generate documentation for: ${args || 'the project'}`;
      case '/audit':
        return `Perform a **full project audit** of the current session${args ? `, focusing on: ${args}` : ''}.

Analyze every file in the session and produce a structured report covering ALL of the following sections:

---

## 📊 Project Audit Report

### 1. Project Overview
- Project name, framework, language, and key dependencies
- Overall project structure summary (file count, folders, entry points)

### 2. Code Quality
- **Errors & Bugs**: Syntax errors, runtime issues, broken imports, unused variables, dead code
- **Type Safety**: Missing types, \`any\` usage, type mismatches (if TypeScript)
- **Best Practices**: Anti-patterns, code smells, DRY violations, overly complex functions
- **Naming & Consistency**: Inconsistent naming conventions, mixed styles

### 3. Pending Issues & TODOs
- List every \`TODO\`, \`FIXME\`, \`HACK\`, \`XXX\`, and \`WARN\` comment found in code
- Known bugs or broken features based on code analysis
- Missing error handling or edge cases
- Incomplete implementations (empty functions, placeholder content)

### 4. Security Audit
- Hardcoded secrets, API keys, tokens, or credentials in code
- XSS vulnerabilities (unsanitized HTML rendering)
- SQL injection risks (if applicable)
- Missing input validation
- Insecure dependencies or known CVEs
- CORS or auth misconfigurations

### 5. Performance
- Large bundle risks (heavy imports, missing code-splitting)
- Unnecessary re-renders (unstable refs, missing memoization)
- N+1 queries or redundant API calls
- Missing lazy loading for images or routes

### 6. Dependency Health
- Outdated dependencies
- Unused dependencies in package.json
- Missing peer dependencies
- Conflicting version ranges

### 7. Progress & Completeness
- What features are fully implemented and working
- What features are partially implemented (WIP)
- What features are planned but not started (based on code structure and TODOs)
- Estimated completion percentage per feature area

### 8. File-by-File Issues
For each file with issues, list:
- File path
- Severity (🔴 Critical / 🟡 Warning / 🔵 Info)
- Description of the issue
- Suggested fix (brief)

### 9. Recommendations
- Top 5 highest-priority fixes (ranked by impact)
- Quick wins that can be done immediately
- Architectural improvements for long-term health

---

**Formatting rules:**
- Use severity badges: 🔴 Critical, 🟡 Warning, 🔵 Info, ✅ Good
- Be specific — reference exact file paths and line context
- Include code snippets for critical issues
- End with a score: Overall Health Score out of 100
- Keep it actionable — every issue should have a clear next step`;

      case '/redesign':
        return `Completely redesign and restyle the current project${args ? ` with focus on: ${args}` : ''}.

Apply the **FRONTEND DESIGN SKILL** and **DESIGN QUALITY GUIDELINES** from your system instructions to create a bold, distinctive redesign. Follow every principle — typography, color, motion, spatial composition, backgrounds, micro-interactions.

**PRESERVE ALL existing functionality, content, usability, and routes.** Only change visual presentation — never remove features, pages, or interactive behaviors.

This is a **full re-skin request**, not a minor polish pass. You MUST:
1. Replace the visual system end-to-end (type scale, font pairings, color tokens, spacing rhythm, radius/shadow language, interaction states).
2. Apply a distinctive font strategy (display font + body font), wire it globally, and ensure all headings/buttons/forms inherit the new system.
3. Add/upgrade visual media (hero/section imagery, backgrounds, illustrations, or branded visual elements) unless user explicitly asks for text-only.
4. Update all key shared UI surfaces (layout shell, nav, cards, buttons, forms, tables, modals, empty states) so the redesign feels coherent everywhere.
5. Remove generic/basic styling patterns and avoid default-looking output.

If you need new packages (framer-motion, lucide-react, etc.), add them to package.json dependencies — the system will auto-install them.

Review ALL existing component files and provide complete updated code for EVERY file that needs visual changes. Each file must be a complete replacement — no partial updates.`;
      case '/regenerate-images':
        return `Regenerate all images in the current project to match the site's design and content${args ? `, with style: ${args}` : ''}.

**Image Generation Steps:**
1. Scan all files for image references (\`<img>\`, \`background-image\`, CSS \`url()\`, etc.)
2. For each image that is a placeholder or AI-generated, create a relevant prompt that matches:
   - The page/section context where the image appears
   - The project's overall color scheme and brand aesthetic
   - The content and purpose of the surrounding section
3. Use the project's image generation API endpoint (\`/_api/images/generate\`) with provider \`modelslab\` to generate each image
   - If ModelsLab is unavailable, fall back to provider \`gemini\`
   - Set \`saveToSession: true\` so images are automatically added to the session image library
   - Use appropriate \`width\` and \`height\` to match original dimensions
   - Use \`style\` parameter (professional, tech, elegant, etc.) to match the site aesthetic
4. **Show each generated image inline** in the chat response using markdown: \`![description](imageUrl)\`
5. Replace image references in the code with the newly generated image URLs
6. Ensure all images have proper alt text for accessibility

**Important:** Every generated image should be:
- Shown visually in your response so the user can preview it
- Automatically saved to the session image library (saveToSession: true)
- Available for download from the image library panel

Generate images that are contextually relevant — hero images should match the site theme, feature images should illustrate the feature, testimonial avatars should look realistic, etc.

Keep the same image dimensions and aspect ratios as the originals.`;

      case '/skill-list':
        return 'List all currently installed agent skills. Use `npx skills list` to show what skills are available.';
      case '/skill-remove':
        return `Remove the agent skill: ${args}. Use \`npx skills remove ${args}\` to uninstall it.`;
      default:
        return args || 'Please help with this request.';
    }
  }

  // Expose sendMessage function to parent via ref
  React.useEffect(() => {
    if (onSendMessageRef) {
      onSendMessageRef(async (message: string) => {
        await handleSend(message, [], undefined);
      });
    }
  }, [onSendMessageRef, selectedModel, mode]);

  // Listen for fix error events from step items
  React.useEffect(() => {
    const handleFixError = (event: CustomEvent<{ stepText: string }>) => {
      const { stepText } = event.detail;
      if (stepText) {
        // Send a fix request to the AI
        const fixPrompt = `Please fix this issue: ${stepText}\n\nAnalyze the error and provide the corrected code.`;
        handleSend(fixPrompt, [], undefined);
      }
    };

    window.addEventListener('tasking.fixError', handleFixError as EventListener);
    return () => {
      window.removeEventListener('tasking.fixError', handleFixError as EventListener);
    };
  }, []);

  // Listen for recommendation selections from modal
  React.useEffect(() => {
    const handleRecommendationSelected = (
      event: CustomEvent<{ message: string; autoSend: boolean }>,
    ) => {
      const { message } = event.detail;
      if (message) {
        handleSend(message, [], undefined);
      }
    };

    window.addEventListener(
      'tasking.recommendationSelected',
      handleRecommendationSelected as EventListener,
    );
    return () => {
      window.removeEventListener(
        'tasking.recommendationSelected',
        handleRecommendationSelected as EventListener,
      );
    };
  }, []);

  const handleSuggestionSelect = async (suggestionCode: string) => {
    // Show processing state while applying suggestion
    setIsApplyingSuggestion(true);
    isAtBottomRef.current = true; // Force scroll to bottom to see progress

    try {
      await applySuggestion(suggestionCode);
    } finally {
      setIsApplyingSuggestion(false);
    }
  };

  // Auto-detect element edit suggestions and dispatch event for session page to handle
  useEffect(() => {
    const historyMessages = history?.messages;
    if (!historyMessages || historyMessages.length === 0) return;

    // Look for recent AI messages that might contain element edit suggestions
    const recentMessages = historyMessages.slice(-3); // Check last 3 messages
    const lastMessage = recentMessages[recentMessages.length - 1];

    if (lastMessage?.role === 'assistant' && lastMessage.content) {
      // Check if this message contains code suggestions (code blocks with filename)
      const codeBlockRegex = /```[\w]*\s*filename="([^"]+)"\n([\s\S]*?)```/g;
      let match;
      const suggestions: Array<{ filename: string; code: string }> = [];

      while ((match = codeBlockRegex.exec(lastMessage.content)) !== null) {
        suggestions.push({
          filename: match[1],
          code: match[2].trim(),
        });
      }

      // If we found code suggestions, dispatch event for element edit confirmation
      if (suggestions.length > 0) {
        const signature = `${sessionId}:${suggestions
          .map((s) => `${s.filename}:${s.code}`)
          .join('||')}`;
        if (lastDispatchedSuggestionSignatureRef.current === signature) return;
        lastDispatchedSuggestionSignatureRef.current = signature;
        suggestions.forEach((suggestion) => {
          window.dispatchEvent(
            new CustomEvent('tasking.elementEditSuggestion', {
              detail: {
                filename: suggestion.filename,
                code: suggestion.code,
                sessionId,
              },
            }),
          );
        });
      }
    }
  }, [history, sessionId]);

  async function handleCreateCheckpoint(name?: string, description?: string) {
    try {
      await createCheckpointMutation.mutateAsync({
        sessionId,
        name: name || `Checkpoint - ${new Date().toLocaleTimeString('en-US')}`,
        description: description || 'Created from AI chat',
        checkpointType: 'manual',
      });
    } catch (error) {
      // Error handling is done in the mutation hook via toast
    }
  }

  const getUserStatus = (userId: number): 'online' | 'meeting' | 'away' | 'offline' | null => {
    // presenceData is an array of participants directly from usePresenceCentralized
    const participant = presenceData?.find((p) => p.userId === userId);
    // If current user and no presence data, assume online since they're viewing the chat
    if (!participant && currentUser?.id === userId) {
      return 'online';
    }
    return participant?.status ?? null;
  };

  const getUserAvatarUrl = (userId: number): string | null => {
    // presenceData is an array of participants directly from usePresenceCentralized
    const participant = presenceData?.find((p) => p.userId === userId);
    // Fallback to current user's avatar from auth context if it's their own message
    if (currentUser?.id === userId) {
      return participant?.avatarUrl ?? currentUser?.avatarUrl ?? null;
    }
    return participant?.avatarUrl ?? null;
  };

  // Build personaId → avatar URL map so message avatars match the participants strip
  const participantAvatarMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (aiParticipants) {
      for (const p of aiParticipants) {
        if (p.isActive) {
          map[p.personaId] = p.avatarUrl || getRobotAvatar(p.personaId);
        }
      }
    }
    return map;
  }, [aiParticipants]);

  const handleRestoreCheckpoint = (checkpointId: string) => {
    setPreviewCheckpointId(checkpointId);
  };

  const handleRestoreConfirm = async () => {
    if (!previewCheckpointId) return;
    try {
      await restoreCheckpointMutation.mutateAsync({
        checkpointId: previewCheckpointId,
      });
      // Close dialog handled by onRestore logic in Dialog component usually,
      // but here the dialog calls onRestore then closes itself or we close it?
      // Looking at CheckpointPreviewDialog:
      // handleRestore calls onRestore(); onOpenChange(false);
      // So we just need to do the async work here.
    } catch (error) {
      // Error handling via toast in hook
    }
  };

  // Handler for viewing file diffs - opens FileDiffViewer with checkpoint comparison
  const handleViewFileDiff = useCallback((checkpointId: string, filename: string) => {
    setDiffViewerState({ open: true, checkpointId, filename });
  }, []);

  // Get checkpoint files for diff comparison
  const checkpointFilesForDiff = useMemo(() => {
    if (!diffCheckpointData?.checkpoint?.filesSnapshot) return [];
    try {
      const snapshot = diffCheckpointData.checkpoint.filesSnapshot;
      if (typeof snapshot === 'string') {
        return JSON.parse(snapshot);
      }
      return Array.isArray(snapshot) ? snapshot : [];
    } catch {
      return [];
    }
  }, [diffCheckpointData?.checkpoint?.filesSnapshot]);

  // IMPORTANT: All hooks must be called before any early returns to follow React's rules of hooks
  // Memoize allMessages to avoid recreating array every render
  // Include both direct streaming message and queued messages
  const queuedUserMessages = useMemo(() => {
    return messageQueue
      .filter((m) => m.status === 'pending' || m.status === 'processing')
      .map((m) => ({
        id: `queue_user_${m.id}`,
        role: 'user' as const,
        content: m.content,
        createdAt: m.createdAt,
        isQueued: true,
        queueStatus: m.status,
        // Include imageAttachment so the image thumbnail shows in queued messages
        imageAttachment: m.attachment || null,
      }));
  }, [messageQueue]);

  const queuedAssistantMessages = useMemo(() => {
    return Array.from(activeStreams.entries())
      .filter(([_, stream]) => stream.content.length > 0)
      .map(([messageId, stream]) => ({
        id: `queue_assistant_${messageId}`,
        role: 'assistant' as const,
        content: stream.content,
        isStreaming: stream.isStreaming,
        createdAt: new Date(),
        isQueued: true,
      }));
  }, [activeStreams]);

  const allMessages = useMemo(() => {
    const dbMessages = history?.messages || [];
    let msgs = dbMessages;
    const hasActiveStreaming =
      streamingMessage || queuedAssistantMessages.some((m) => m.isStreaming);
    // When streaming is active (either main path or queue path), strip trailing
    // assistant messages from DB history to avoid duplicates with streaming state.
    // The backend creates/updates assistant messages in DB during streaming,
    // but the frontend streaming state already has the latest content.
    if (hasActiveStreaming) {
      while (msgs.length > 0 && msgs[msgs.length - 1].role === 'assistant') {
        msgs = msgs.slice(0, -1);
      }
    }

    // Deduplicate DB assistant messages: if the same persona posts near-identical
    // content (first 200 chars match) within the same batch, keep only the first.
    // This catches duplicates from the backend double-call bug and race conditions.
    const seenAssistantKeys = new Set<string>();
    msgs = msgs.filter((m: any) => {
      if (m.role !== 'assistant' || !m.content) return true;
      const key = `${m.personaId || 'main'}::${(m.content as string).slice(0, 200)}`;
      if (seenAssistantKeys.has(key)) return false;
      seenAssistantKeys.add(key);
      return true;
    });

    // Deduplicate queued assistant messages against DB history.
    // When a queued stream completes, the DB gets the message via invalidateQueries.
    // Until the stream is cleared from activeStreams, both would appear.
    // Use content prefix matching (first 200 chars) to detect duplicates.
    const dbAssistantContents = new Set(
      dbMessages
        .filter((m: any) => m.role === 'assistant' && m.content)
        .map((m: any) => (m.content as string).slice(0, 200)),
    );
    const dedupedQueuedAssistant = queuedAssistantMessages.filter((qm) => {
      // Always keep actively streaming messages
      if (qm.isStreaming) return true;
      // For completed streams, exclude if DB already has this content
      const prefix = qm.content.slice(0, 200);
      return !dbAssistantContents.has(prefix);
    });

    return [
      ...msgs,
      ...(streamingMessage ? [streamingMessage] : []),
      ...queuedUserMessages,
      ...dedupedQueuedAssistant,
    ];
  }, [history?.messages, streamingMessage, queuedUserMessages, queuedAssistantMessages]);

  // Auto-scroll to bottom when queued messages update (streaming content)
  useEffect(() => {
    scheduleScrollToBottom();
  }, [queuedAssistantMessages]);


  // Simple markdown to HTML converter for system messages
  const formatMarkdownBasic = (md: string): string => {
    return md
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/^\| (.+) \|$/gim, (match, content) => {
        const cells = content
          .split(' | ')
          .map((c: string) => `<td>${c.trim()}</td>`)
          .join('');
        return `<tr>${cells}</tr>`;
      })
      .replace(/^\|[-:|]+\|$/gim, '') // Remove table separator rows
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>')
      .replace(/^---$/gim, '<hr>');
  };

  const [showAdvancedFrameworks, setShowAdvancedFrameworks] = useState(false);

  const handleFrameworkKickoff = useCallback(
    async (
      framework:
        | 'react'
        | 'vue'
        | 'next'
        | 'python'
        | 'svelte'
        | 'angular'
        | 'express'
        | 'laravel'
        | 'flask'
        | 'fastapi'
        | 'django'
        | 'flutter'
        | 'rust'
        | 'go'
        | 'three'
        | 'playcanvas'
        | 'godot'
        | 'idk',
    ) => {
      const prompts: Record<typeof framework, string> = {
        react:
          'I want to build in React + Vite. Set up the project structure and start with a production-quality starter.',
        vue: 'I want to build in Vue + Vite. Set up the project structure and start with a production-quality starter.',
        next: 'I want to build in Next.js. Set up the project structure and start with a production-quality starter.',
        python:
          'I want to build in Python. Ask whether Flask, FastAPI, or Django fits best, then scaffold the recommended option.',
        svelte:
          'I want to build in Svelte + Vite. Set up the project structure and start with a production-quality starter.',
        angular:
          'I want to build in Angular. Set up the project structure and start with a production-quality starter.',
        express:
          'I want to build in Express. Set up a Node + Express starter with clear API route structure.',
        laravel:
          'I want to build in Laravel. Scaffold a Laravel-style starter structure and explain local run steps.',
        flask:
          'I want to build in Flask. Set up a Flask starter with a clean API/server structure.',
        fastapi:
          'I want to build in FastAPI. Set up a FastAPI starter with typed request/response models.',
        django:
          'I want to build in Django. Set up a Django starter with project and app structure.',
        flutter:
          'I want to build in Flutter. Set up a Flutter starter and structure the app for easy feature expansion.',
        rust: 'I want to build in Rust. Set up a Rust starter and explain the build/run workflow.',
        go: 'I want to build in Go. Set up a Go starter and structure it for maintainable growth.',
        three:
          'I want to build with Three.js. Scaffold a 3D scene starter with clean render loop and structure.',
        playcanvas:
          'I want to build with PlayCanvas. Scaffold a PlayCanvas starter with scene, camera, light, and entity setup.',
        godot:
          'I want to build in Godot. Scaffold a Godot project structure and explain how to continue in Godot editor.',
        idk: "I don't know which framework to use. Ask me 3 quick questions, recommend the best framework, then scaffold the starter.",
      };
      await handleSend(prompts[framework], [], undefined);
    },
    [handleSend],
  );

  if (isLoading) {
    return (
      <div className={`${styles.container} ${className || ''}`}>
        <div className={styles.messages}>
          <div className={styles.messageWrapper}>
            <Skeleton className={styles.avatarSkeleton} />
            <div className={styles.contentSkeleton}>
              <Skeleton style={{ width: '60%', height: '1rem', marginBottom: '0.5rem' }} />
              <Skeleton style={{ width: '80%', height: '1rem' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load chat history';
    return (
      <div className={`${styles.container} ${className || ''}`}>
        <div className={styles.messages}>
          <div className={styles.emptyState}>
            <AlertCircle size={48} className={styles.errorIcon} />
            <div className={styles.errorContent}>
              <p className={styles.errorText}>{errorMessage}</p>
              <p className={styles.subtext}>
                {errorMessage === 'Not authenticated'
                  ? 'Please log in to access the AI chat.'
                  : 'Please try refreshing the page or check your connection.'}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
                style={{ marginTop: '12px' }}
              >
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check for incomplete messages (assistant messages without checkpoints that are recent)
  // These might be messages that were streaming when the page was refreshed
  const incompleteMessages = (history?.messages || []).filter(
    (msg: any) =>
      msg.role === 'assistant' &&
      !msg.checkpointId &&
      msg.createdAt &&
      Date.now() - new Date(msg.createdAt).getTime() < 300000, // Last 5 minutes
  );

  // If we have an incomplete message and no active streaming, treat it as potentially streaming
  // Also consider AI activity from the queue (other participants may be running AI)
  const hasIncompleteMessage =
    (incompleteMessages.length > 0 && !streamingMessage) || hasAiActivity;

  return (
    <div className={`${styles.panel} chat-panel ${className || ''}`}>
      {/* Voice controls are minimal - just mute/volume inline, no separate panel */}
      {/* AI Activity Indicator - shows when AI is working (visible to all participants) */}
      {hasAiActivity && currentActivity && !streamingMessage && (
        <div className={styles.aiActivityBanner}>
          <Loader2 size={16} className={styles.aiActivitySpinner} />
          <span className={styles.aiActivityText}>Tasking.tech AI is working...</span>
          {currentActivity.currentStep && (
            <span className={styles.aiActivityStep}>{currentActivity.currentStep}</span>
          )}
        </div>
      )}

      {/* Tip Banner removed — user preference */}

      {/* Active AI Participants Bar */}
      {hasActiveParticipants && aiParticipants && (
        <div className={styles.participantsStrip}>
          <span className={styles.participantsStripLabel}>AI Team</span>
          <div className={styles.participantsStripAvatars}>
            {aiParticipants
              .filter((p) => p.isActive)
              .map((p) => (
                <span
                  key={p.id}
                  className={styles.participantsStripChip}
                  title={p.personaName}
                  aria-label={p.personaName}
                >
                  <img
                    src={p.avatarUrl || getRobotAvatar(p.personaId)}
                    alt=""
                    className={styles.participantsStripAvatar}
                  />
                </span>
              ))}
          </div>
        </div>
      )}

      {/* TTS Queue — shows who's speaking and who's next */}
      {ttsQueue.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
          background: 'rgba(249,115,22,0.08)', borderBottom: '1px solid rgba(249,115,22,0.15)',
          fontSize: '0.75rem', color: 'var(--muted-foreground)',
          cursor: 'pointer',
        }}
          onClick={() => {
            const names = ttsQueue.map((e, i) => `${i === 0 ? '🔊' : '⏳'} ${e.personaName}: "${e.preview}"`).join('\n');
            toast.info(`Voice Queue (${ttsQueue.length}):\n${names}`, { duration: 5000 });
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            🔊 <strong>{ttsQueue[0]?.personaName}</strong> speaking
          </span>
          {ttsQueue.length > 1 && (
            <span style={{ opacity: 0.7 }}>
              + {ttsQueue.length - 1} in queue
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); stopSpeaking(); setTtsQueue([]); ttsQueueRef.current = Promise.resolve(); }}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--muted-foreground)',
              cursor: 'pointer', fontSize: '0.75rem', padding: '2px 6px',
            }}
          >
            Stop All
          </button>
        </div>
      )}

      {/* Chat Messages - each AI response split into separate bubbles */}
      <div
        className={`${styles.panel__body} ${styles.chatPanel__body}`}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {/* Local System Message (from slash commands) */}
        {localSystemMessage && (
          <div className={styles.systemMessage}>
            <div className={styles.systemMessageContent}>
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatMarkdownBasic(localSystemMessage)) }} />
            </div>
            <button
              className={styles.systemMessageClose}
              onClick={() => setLocalSystemMessage(null)}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        )}

        {allMessages.length === 0 ? (
          <div className={styles.emptyState}>
            <Bot size={48} className={styles.emptyIcon} />
            <p>Start a conversation with the AI assistant.</p>
            <p className={styles.subtext}>
              Ask for code reviews, explanations, or planning assistance.
            </p>
            <div className={styles.frameworkChooser}>
              <p className={styles.frameworkChooserTitle}>
                What framework would you like to build in?
              </p>
              <div className={styles.frameworkChooserButtons}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleFrameworkKickoff('react')}
                    >
                      ⚛️ React
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Fast UI/webapp development with Vite</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleFrameworkKickoff('vue')}
                    >
                      🖖 Vue
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Vue 3 + Vite for component-driven apps</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleFrameworkKickoff('next')}
                    >
                      ▲ Next.js
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>SSR-first React framework with App Router</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleFrameworkKickoff('python')}
                    >
                      🐍 Python
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    General Python path; AI can choose Flask/FastAPI/Django
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" onClick={() => void handleFrameworkKickoff('idk')}>
                      I Don&apos;t Know
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>AI asks quick questions, recommends the best fit</TooltipContent>
                </Tooltip>
              </div>
              <div className={styles.frameworkChooserAdvancedToggle}>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowAdvancedFrameworks((v) => !v)}
                >
                  {showAdvancedFrameworks ? 'Hide advanced options' : 'More frameworks'}
                </Button>
              </div>
              {showAdvancedFrameworks && (
                <div className={styles.frameworkChooserAdvanced}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('svelte')}
                      >
                        🔥 Svelte
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Svelte + Vite with minimal runtime overhead</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('angular')}
                      >
                        🅰️ Angular
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Full TypeScript framework for large apps</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('express')}
                      >
                        🧩 Express
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Node.js API/server starter</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('laravel')}
                      >
                        🔺 Laravel
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>PHP framework with batteries included</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('flask')}
                      >
                        🌶️ Flask
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Lightweight Python web framework</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('fastapi')}
                      >
                        ⚡ FastAPI
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>High-performance async Python API framework</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('django')}
                      >
                        🎸 Django
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Full-stack Python framework with admin/auth</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('flutter')}
                      >
                        📱 Flutter
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Cross-platform mobile/web app framework</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('rust')}
                      >
                        🦀 Rust
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Systems-level performance and safety</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('go')}
                      >
                        🐹 Go
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Simple, fast backend services</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('three')}
                      >
                        🕹️ Three.js
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>WebGL-based 3D scenes and experiences</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('playcanvas')}
                      >
                        🎮 PlayCanvas
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>3D game framework with scene/entity workflow</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleFrameworkKickoff('godot')}
                      >
                        🐉 Godot
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Game engine workflow with GDScript scenes</TooltipContent>
                  </Tooltip>
                </div>
              )}
              <div className={styles.frameworkChooserHint}>
                Hover a button to see what it is best for.
              </div>
            </div>
            <p className={styles.cmdHint}>
              Type <code>/cmdlist</code> to see available commands
            </p>
          </div>
        ) : (
          <>
            {allMessages.map((msg, idx) => {
              // Check if this message is potentially still streaming (incomplete)
              // A message is incomplete if: it's an assistant message, has no checkpoint, is recent (within 5 min)
              // and we're not the one streaming (checked via hasIncompleteMessage)
              const isIncomplete =
                hasIncompleteMessage &&
                msg.role === 'assistant' &&
                !msg.checkpointId &&
                msg.createdAt &&
                Date.now() - new Date(msg.createdAt).getTime() < 300000;
              const isStreamingMsg = msg.id === streamingMessage?.id || isIncomplete;

              // For user messages, render normally
              if (msg.role === 'user') {
                return (
                  <AiChatMessage
                    key={msg.id || `msg-${idx}`}
                    message={msg}
                    sessionId={sessionId}
                    isStreaming={false}
                    userStatus={'userId' in msg ? getUserStatus(msg.userId) : undefined}
                    userAvatarUrlFallback={
                      'userId' in msg ? getUserAvatarUrl(msg.userId) : undefined
                    }
                    currentFile={currentFile}
                    currentUserId={currentUser?.id}
                    currentUserDisplayName={currentUser?.displayName ?? null}
                    participantAvatarMap={participantAvatarMap}
                  />
                );
              }

              // Render assistant messages as a SINGLE bubble (no splitting into thinking + steps)
              // The AiChatMessage component handles collapsible sections internally
              return (
                <AiChatMessage
                  key={msg.id || `msg-${idx}`}
                  message={msg}
                  sessionId={sessionId}
                  isStreaming={isStreamingMsg}
                  onCreateCheckpoint={handleCreateCheckpoint}
                  onRestoreCheckpoint={handleRestoreCheckpoint}
                  onSuggestionSelect={handleSuggestionSelect}
                  onViewFileDiff={handleViewFileDiff}
                  currentFile={currentFile}
                  currentUserId={currentUser?.id}
                  currentUserDisplayName={currentUser?.displayName ?? null}
                  autoApplyRecommendations={sessionData?.session?.autoApplyRecommendations ?? false}
                  participantAvatarMap={participantAvatarMap}
                  onRegenerate={(prompt) => {
                    void handleSend(prompt, [], undefined);
                  }}
                />
              );
            })}
            {/* Show processing indicator when applying a suggestion */}
            {isApplyingSuggestion && (
              <div className={styles.processingIndicator}>
                <Spinner size="sm" />
                <span>Creating backup... then applying changes</span>
              </div>
            )}
          </>
        )}
      </div>

      <CheckpointPreviewDialog
        checkpointId={previewCheckpointId}
        open={!!previewCheckpointId}
        onOpenChange={(open) => !open && setPreviewCheckpointId(null)}
        onRestore={handleRestoreConfirm}
      />

      <CommandListModal open={isCmdListOpen} onOpenChange={setIsCmdListOpen} />

      {/* File Diff Viewer - shows diff between checkpoint and current files */}
      <FileDiffViewer
        open={diffViewerState.open}
        onOpenChange={(open) =>
          !open && setDiffViewerState({ open: false, checkpointId: null, filename: null })
        }
        currentFiles={sessionFiles}
        checkpointFiles={checkpointFilesForDiff}
        checkpointName={diffCheckpointData?.checkpoint?.name || 'Before Changes'}
        initialFile={diffViewerState.filename || undefined}
      />

      <div className={`${styles.panel__footer} ${styles.chatPanel__footer}`}>
        {/* Queue status indicator - show when messages are queued */}
        {(pendingCount > 0 || processingCount > 0) && (
          <div className={styles.queueIndicator}>
            <div className={styles.queueStatus}>
              <Clock size={14} />
              <span>
                {processingCount > 0 && `Processing ${processingCount}`}
                {processingCount > 0 && pendingCount > 0 && ' • '}
                {pendingCount > 0 && `${pendingCount} queued`}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={stopAll} className={styles.queueStopBtn}>
              Stop All
            </Button>
          </div>
        )}
        <AiChatInput
          onSend={handleSend}
          disabled={false} // Never disable - allow non-blocking chat like Claude Code
          currentFile={currentFile}
          annotations={annotations}
          sessionId={sessionId}
          onStopGeneration={
            streamingMessage ? stopGeneration : processingCount > 0 ? stopAll : undefined
          }
          initialAttachment={initialAttachment}
          onRequestAnnotateAttachment={onRequestAnnotateAttachment}
          onSaveImageToFiles={undefined}
          mode={mode}
          onModeChange={setMode}
          webSearchEnabled={webSearchEnabled}
          onWebSearchToggle={() => setWebSearchEnabled(!webSearchEnabled)}
          autoExecute={autoExecuteValue}
          onAutoExecuteChange={(v) => {
            if (onAutoExecuteChange) onAutoExecuteChange(v);
            else setLocalAutoExecute(v);
          }}
          selectedModel={selectedModel}
          onModelChange={handleModelChange}
          isOwner={isOwner}
          hasQueuedMessages={pendingCount > 0}
          processingCount={processingCount}
          activeSkills={activeSkills}
          onActiveSkillsChange={onActiveSkillsChange}
          onOpenSkillSearch={onOpenSkillSearch}
        />
      </div>
    </div>
  );
};
